import React, { memo } from 'react';
import { Layout, Loader } from '@axiom/ui';

import type { MatchingCandidate } from '../../api/chat-api';
import type { ChatTurnGroup } from '../../types/chat.types';
import { ChatMessageOriginators } from '../../enums/chat-message-originators';
import { ChatScreenUtil } from '../../utils/chat-screen-util';

import { ChatMetaData } from './ChatMetaData';
import { ChatTextarea } from './ChatTextarea';

export type ChatTurnProps = {
  turn: ChatTurnGroup;
  hasRoom: boolean;
  isLastTurn: boolean;
  isScrolledTurn: boolean;
  showCandidates: (candidates: MatchingCandidate[]) => void;
  showLoading: boolean;
  lastTurnRef: React.MutableRefObject<HTMLDivElement | null>;
  scrolledTurnRef: React.MutableRefObject<HTMLDivElement | null>;
};

// Memoized so composer keystrokes don't re-render every Lexical message; every prop must stay stable per keystroke.
export const ChatTurn = memo(
  ({
    turn,
    hasRoom,
    isLastTurn,
    isScrolledTurn,
    showCandidates,
    showLoading,
    lastTurnRef,
    scrolledTurnRef,
  }: ChatTurnProps) => {
    return (
      <div
        data-test={`CHAT_TURN_${turn.id}`}
        // Must not return a value: React 19 treats it as a cleanup function.
        ref={node => {
          if (isScrolledTurn) {
            scrolledTurnRef.current = node;
          }

          if (isLastTurn) lastTurnRef.current = node;
        }}
      >
        <Layout
          direction="vertical"
          minHeight={hasRoom ? ChatScreenUtil.roomForAnAnswer() : undefined}
          name={`CHAT_TURN_ENTRIES_${turn.id}`}
          verticalGutter="16px"
        >
          {turn.entries.map(({ entry, idx }) => {
            if (entry.messageOriginator === ChatMessageOriginators.METADATA) {
              return <ChatMetaData key={entry.id} entry={entry} />;
            }

            return (
              <ChatTextarea
                entry={entry}
                idx={idx}
                key={entry.id}
                showCandidates={showCandidates}
              />
            );
          })}

          {showLoading ? <Loader name="LOADING" type="dots" /> : null}
        </Layout>
      </div>
    );
  }
);
